/**
 * Widget V2 - Register View
 *
 * New user registration form.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";
import { CardBackButton, GoogleSignInButton, PhoneField, isValidPhone, toE164, useGoogleSignIn } from "/imports/ui/components/shared";

/**
 * Register View component
 */
function RegisterView() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, saasId } = useWidget();
  const { register, error: authError, clearError } = useAuth();
  const { filters } = useUrlFilters({});

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Pre-checked: the channel strategy is default-in with a frictionless opt-out
  // (STOP button on every marketing message). Unticking sends nothing → "unknown".
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);

  const { handleCode, pending: googlePending } = useGoogleSignIn({ onError: setError });

  // Handle field change
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    clearError();

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
      setError("Please fill in all required fields");
      return;
    }

    if (!isValidPhone(formData.phone)) {
      setError("Please enter a valid phone number");
      return;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: toE164(formData.phone),
        password: formData.password,
        saasId,
        slug,
        // Unchecked sends nothing — consent stays "unknown" rather than opted out
        ...(whatsappOptIn ? { whatsappOptIn: true } : {})
      });

      // Collect preferences before sending them on. A new account still has to
      // verify its email afterwards (afterSave: "login"); an already-verified
      // one goes straight into the app (afterSave: "home"). With no token we
      // fall back to the original routing.
      const redirect = filters.redirect || "";
      if (result?.preferencesToken) {
        navigate(`/${slug}/preferences`, {
          state: {
            preferencesToken: result.preferencesToken,
            afterSave: result?.needsVerification ? "login" : "home",
            redirect,
            email: formData.email
          }
        });
      } else if (result?.needsVerification) {
        navigate(`/${slug}/check-email`, { state: { email: formData.email, redirect } });
      } else {
        enqueueSnackbar("Account created successfully!", { variant: "success" });
        navigate(`/${slug}/${redirect}`);
      }
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={4}>
      <Card sx={{ maxWidth: "31.25rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <CardBackButton onClick={() => navigate(`/${slug}/signup${filters.redirect ? `?redirect=${filters.redirect}` : ""}`)} />
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Create Account
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            Enter your details to create a new account
          </Typography>

          {/* Error message */}
          {(error || authError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || authError}
            </Alert>
          )}

          <GoogleSignInButton onCode={handleCode} text="signup_with" disabled={loading || googlePending} />

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>

          {/* Registration form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={formData.firstName} onChange={handleChange("firstName")} required autoFocus />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={formData.lastName} onChange={handleChange("lastName")} required />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  autoComplete="email"
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneField value={formData.phone} onChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))} required />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange("password")}
                  required
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          onMouseDown={(e) => e.preventDefault()}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  required
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          onMouseDown={(e) => e.preventDefault()}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ mt: 1.5, alignItems: "flex-start", ".MuiCheckbox-root": { pt: 0 } }}
              control={<Checkbox checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)} />}
              label={
                <Typography variant="body2" color="textSecondary">
                  Send me club offers &amp; updates (you can opt out anytime)
                </Typography>
              }
            />

            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || googlePending} sx={{ mt: 3, mb: 2 }}>
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default RegisterView;
